import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { SettingsSelectors } from '@store/settings';
import { AuthSelectors } from '@store/auth';
import { ChatwootAccessMode, ChatwootContext, ChatwootModule, ChatwootRouteData } from '@shared/types/chatwoot-context';
import { ChatwootEntityRefService, ChatwootEntityRef } from './chatwoot-entity-ref.service';

@Injectable({ providedIn: 'root' })
export class ChatwootContextMapperService {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly settingsSelectors = inject(SettingsSelectors);
    private readonly authSelectors = inject(AuthSelectors);
    private readonly entityRef = inject(ChatwootEntityRefService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly contextSubject = new BehaviorSubject<ChatwootContext | null>(null);

    readonly context$: Observable<ChatwootContext> = this.contextSubject
        .asObservable()
        .pipe(filter((ctx): ctx is ChatwootContext => ctx !== null));

    deriveAccessMode(email: string | null | undefined): ChatwootAccessMode {
        if (!email) {
            return 'customer';
        }

        return email.toLowerCase().endsWith('@unilog.company') ? 'internal' : 'customer';
    }

    static extractRouteContext(snapshot: ActivatedRouteSnapshot): {
        routeData: ChatwootRouteData | null;
        entityId: string | null;
    } {
        let deepest: ActivatedRouteSnapshot | null = snapshot;
        let found: { snap: ActivatedRouteSnapshot; data: ChatwootRouteData } | null = null;

        while (deepest) {
            const data = deepest.data?.['chatwoot'] as ChatwootRouteData | undefined;

            if (data) {
                found = { snap: deepest, data };
            }

            deepest = deepest.firstChild;
        }

        if (!found) {
            return { routeData: null, entityId: null };
        }

        const entityId = found.data.entityIdParam ? found.snap.paramMap.get(found.data.entityIdParam) : null;

        return { routeData: found.data, entityId };
    }

    start(): void {
        combineLatest([
            this.router.events.pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                startWith({ urlAfterRedirects: this.router.url } as NavigationEnd)
            ),
            this.settingsSelectors.selectedPartners$,
            this.authSelectors.user$,
            this.entityRef.stream$
        ])
            .pipe(
                map(([nav, partners, user, entityRef]) => this.build(nav, partners, user, entityRef)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(ctx => this.contextSubject.next(ctx));
    }

    private build(
        nav: NavigationEnd,
        partners: Array<{ id: number; slug?: string; display_name?: string }>,
        user: {
            email?: string;
            organisation_id?: number;
            organisation?: { slug?: string };
            roles?: Array<{ name?: string }>;
        } | null,
        entityRef: ChatwootEntityRef | null
    ): ChatwootContext {
        const rootSnap = this.router.routerState.snapshot.root;
        const { routeData, entityId } = ChatwootContextMapperService.extractRouteContext(rootSnap);
        const activePartner = partners[0] ?? null;
        const organisationSlug = this.readParamDeep(rootSnap, 'organisation');
        const currentUrl = nav.urlAfterRedirects ?? this.router.url;

        const entity_type = routeData?.entityType ?? null;
        const entity_id = entityId;
        const entity_reference =
            entityRef && entity_type && entityRef.type === entity_type && entityRef.id === entity_id
                ? entityRef.reference
                : null;

        // URL is source of truth for shared components (CLAUDE.md §7.3)
        const urlModule = this.deriveModuleFromUrl(currentUrl);

        return {
            source_system: 'logivice',
            organisation_id: user?.organisation_id ?? null,
            organisation_slug: user?.organisation?.slug ?? organisationSlug,
            partner_id: activePartner?.id ?? null,
            partner_slug: activePartner?.slug ?? null,
            partner_name: activePartner?.display_name ?? null,
            module: urlModule ?? routeData?.module ?? null,
            page_type: routeData?.pageType ?? null,
            entity_type,
            entity_id,
            entity_reference,
            current_route: currentUrl,
            user_role: user?.roles?.[0]?.name ?? null,
            access_mode: this.deriveAccessMode(user?.email),
            initiated_from: 'logivice_widget'
        };
    }

    private deriveModuleFromUrl(url: string): ChatwootModule | null {
        if (url.includes('/ucontrol/')) return 'ucontrol';
        if (url.includes('/uops/')) return 'uops';
        if (url.includes('/cockpit/')) return 'cockpit';
        if (url.includes('/book/')) return 'book';

        return null;
    }

    private readParamDeep(snapshot: ActivatedRouteSnapshot, key: string): string | null {
        let node: ActivatedRouteSnapshot | null = snapshot;

        while (node) {
            const value = node.paramMap.get(key);

            if (value) {
                return value;
            }

            node = node.firstChild;
        }

        return null;
    }
}
